import { Component, ReactElement, Fragment } from "react";
import * as React from "react";

import TableRow, { TableRowProps } from "./TableRow";
import TableCell from "./TableCell";
import moment from "moment";
import { CSS_BREAKPOINT_SMALL, FRIENDLY_DATETIME_FORMAT } from "../utils/Utils";
import { DirectionalHint, Icon, Spinner, TooltipDelay, TooltipHost } from "office-ui-fabric-react";

import { AutoSizer, List } from "react-virtualized";
import * as _ from "lodash";

type cellType = "date" | "durationSeconds";

export interface TableItem {
    [key: string]: any;
}

export interface TableColumn {
    title: string;
    extraDetail?: string;
    propertyPath: string;
    type?: cellType;
    enableSort?: boolean;
    sortFunc?: (a: TableItem, b: TableItem) => number;
    sortCallback?: (desc: boolean) => void;
    valueFormatter?: (value: any) => string;
    flex: number;

    //All columns shown on all screens sizes unless configured below
    showSmallScreen?: boolean;
    showLargeScreen?: boolean;
}

interface TableProps {
    title?: string;
    columns: TableColumn[];
    items: TableItem[];
    selectedItem?: TableItem;
    className?: string;
    enableSort?: boolean;
    loading?: boolean;
    emptyMessage?: string;
    rowPropsForItem?(item: TableItem): TableRowProps;
    cellForColumn?(item: TableItem, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined;
    cellForHeader?(column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined;
    hideHeaderTitle?: boolean;
    sortAsc?: boolean;
    defaultSortColumnPath?: string;

    virtualize?: boolean

    enableToolTip?: boolean
    toolTipDescription?: string
    toolTipTitle?: string
    onSortChange?: (sortedItems: TableItem[]) => void
}

interface TableState {
    sortedItems: TableItem[];
    sortAsc: boolean;
    sortColumn?: TableColumn;
    isSmallScreen: boolean;
    showLargeScreenColumnsEvenSmallScreen: boolean;
}

export default class Table extends Component<TableProps, TableState>
{
    virtualListRef: any;
    smallScreenMql?: MediaQueryList;

    constructor(props: Readonly<TableProps>) {
        super(props);

        this.virtualListRef = React.createRef();

        this.state = {
            sortColumn: undefined,
            sortAsc: false,
            sortedItems: [],
            isSmallScreen: false,
            showLargeScreenColumnsEvenSmallScreen: false
        }
    }

    public componentDidMount() {
        if (this.props.items) {
            this.setSortedItems();
        }

        if (this.props.sortAsc != undefined) {
            this.setState({ sortAsc: this.props.sortAsc })
        }

        if (this.props.columns != undefined && this.props.columns.length > 1) {
            const defaultSortColumn = this.props.columns.filter((col) => col.propertyPath === this.props.defaultSortColumnPath)[0];
            if (defaultSortColumn != null) {
                this.setState({ sortColumn: defaultSortColumn });
            }
        }

        this.smallScreenMql = window.matchMedia(`(max-width: ${CSS_BREAKPOINT_SMALL})`);
        const isSmallScreen = this.smallScreenMql.matches;
        this.setState({
            isSmallScreen: isSmallScreen
        });

        this.smallScreenMql.addEventListener("change", this.OnScreenChanged);
    }

    componentWillUnmount() {
        this.smallScreenMql?.removeEventListener("change", this.OnScreenChanged)
    }

    private OnScreenChanged = (e: any): any => {
        this.setState({
            isSmallScreen: e.matches
        });
    }

    componentDidUpdate(prevProps: Readonly<TableProps>, prevState: Readonly<TableState>, snapshot?: any): void {

        if (this.props.items !== prevProps.items ||
            this.props.enableSort !== prevProps.enableSort ||
            this.state.sortColumn !== prevState.sortColumn ||
            this.state.sortAsc !== prevState.sortAsc) {
            this.setSortedItems();
        }

        if (this.props.selectedItem !== prevProps.selectedItem || this.state.isSmallScreen !== this.state.isSmallScreen || this.state.showLargeScreenColumnsEvenSmallScreen !== prevState.showLargeScreenColumnsEvenSmallScreen) {
            this.virtualListRef.current?.forceUpdateGrid();
        }
    }

    private setSortedItems(): void {
        if (!this.props.items) {
            return;
        }

        if (!this.props.enableSort || !this.state.sortColumn) {
            this.setState({
                sortedItems: this.props.items,
            });
            this.virtualListRef.current?.forceUpdateGrid();
            return;
        }

        let sortedItems = [];
        const sortPropertyPath = this.state.sortColumn.propertyPath;
        var column = this.getTableColumn(sortPropertyPath);

        if (column?.sortCallback !== undefined) {
            this.setState({
                sortedItems: this.props.items,
            });
            this.virtualListRef.current?.forceUpdateGrid();
            return;
        }

        if (column != undefined && column.sortFunc != undefined) {
            sortedItems = [...this.props.items].sort(column.sortFunc);
        } else {
            sortedItems = [...this.props.items].sort((a: any, b: any) => {

                let aValue = _.get(a, sortPropertyPath)
                let bValue = _.get(b, sortPropertyPath)

                if (aValue == undefined) {
                    return -1;
                }

                if (bValue == undefined) {
                    return 1;
                }

                return aValue < bValue ? 1 : -1;
            });
        }

        if (!this.state.sortAsc) {
            sortedItems.reverse();
        }

        this.setState({
            sortedItems
        });

        this.virtualListRef.current?.forceUpdateGrid();
        this.props.onSortChange && this.props.onSortChange(sortedItems);
    }

    private buildRows(): ReactElement<TableRow>[] {
        if (!this.props.items) {
            return [];
        }

        return this.state.sortedItems.map((item: TableItem, index: number) => {
            return this.buildRow(item, index);
        });
    }

    private buildRow(item: TableItem, index: number, style?: any): ReactElement<TableRow> {
        const rowProps = this.props.rowPropsForItem ? this.props.rowPropsForItem(item) : {};

        return (
            <TableRow key={index} {...rowProps} selected={this.props.selectedItem === item} style={style}>
                {this.buildRowCells(item, index)}
            </TableRow>
        );
    }

    private getTableColumn = (propertyPath: string): TableColumn | undefined => {
        return this.props.columns.find((t: TableColumn) => t.propertyPath == propertyPath);
    }

    private columnsForScreenSize(): TableColumn[] {

        const showSmallScreenColumns = this.state.isSmallScreen && !this.state.showLargeScreenColumnsEvenSmallScreen;

        return this.props.columns.filter(column => {

            if (showSmallScreenColumns && column.showSmallScreen === false) {
                return false;
            }

            if (!showSmallScreenColumns && column.showLargeScreen === false) {
                return false;
            }

            return true;
        });
    }

    private buildRowCells(item: TableItem, itemIndex: number): ReactElement<TableCell>[] {

        return this.columnsForScreenSize().map((column: TableColumn, columnIndex: number) => {

            if (this.props.cellForColumn) {
                var customCell = this.props.cellForColumn(item, column, columnIndex);
                if (customCell !== undefined) {
                    return customCell;
                }
            }

            let value = _.get(item, column.propertyPath);
            var cellContent = null;

            if (typeof value === "boolean") {
                cellContent = <span>{value ? "yes" : "no"}</span>;
            }
            else if (column.type === "date" || value instanceof Date) {
                if (value) {
                    cellContent = (
                        <Fragment>
                            <span>{moment(value).fromNow()}</span>
                            <span className="cdb-table__extra-detail">{moment(value).format(FRIENDLY_DATETIME_FORMAT)}</span>
                        </Fragment>
                    );
                }
            }
            else if (column.type === "durationSeconds") {
                if (value) {
                    cellContent = (
                        <Fragment>
                            <span>{moment.duration(value, 'seconds').humanize()}</span>
                        </Fragment>
                    );
                }
            }
            else {
                if (column.valueFormatter) {
                    value = column.valueFormatter(value);
                }

                if (value instanceof Object) {
                    value = value.toString();
                }
                cellContent = <span>{value}</span>
            }

            return (
                <TableCell key={columnIndex} flex={column.flex}>
                    {cellContent}
                </TableCell>
            );
        });
    }

    private onHeaderCellClicked = (columnClicked: TableColumn) => {
        let sortAsc = true;
        if (this.state.sortColumn === columnClicked) {
            sortAsc = !this.state.sortAsc
            this.setState({
                sortAsc: sortAsc
            });
        }
        else {
            this.setState({
                sortColumn: columnClicked,
                sortAsc: sortAsc
            });
        }

        if (columnClicked.sortCallback != null) {
            columnClicked.sortCallback(!sortAsc);
        }

    }

    private buildHeaderCells(): ReactElement<TableCell>[] {
        return this.columnsForScreenSize().map((column: TableColumn, index: number) => {
            if (this.props.cellForHeader) {
                var customCell = this.props.cellForHeader(column, index);
                if (customCell !== undefined) {
                    return customCell;
                }
            }

            const enableClick = (this.props.enableSort && column.enableSort) || column.sortCallback !== undefined;
            const onClick = enableClick ? () => this.onHeaderCellClicked(column) : undefined;

            return (
                <TableCell key={index} flex={column.flex} onClick={onClick} className={enableClick ? "clickable" : ""}>
                    <span>{column.title}</span>
                    {column.extraDetail ? <span className={"cdb-table__extra-detail"}>{column.extraDetail}</span> : null}
                </TableCell>
            );
        });
    }

    private virtualizedRowRenderer = (options: { index: number, style: any }): ReactElement<TableRow> => {
        const index = options.index;
        return this.buildRow(this.state.sortedItems[index], index, options.style)
    }

    private buildRowsOrVirtualizedList() {
        if (this.props.virtualize) {
            return (
                <div className={"flex-row flex-item--flex-1"}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <List
                                ref={this.virtualListRef}
                                height={height}
                                rowCount={this.state.sortedItems ? this.state.sortedItems.length : 0}
                                rowHeight={44}
                                rowRenderer={this.virtualizedRowRenderer}
                                width={width}
                                scrollToIndex={_.indexOf(this.state.sortedItems, this.props.selectedItem)}
                            >
                            </List>
                        )}
                    </AutoSizer>
                </div>
            );
        }
        else {
            return this.buildRows();
        }
    }

    private buildHeader() {

        const title = this.props.title || "";
        const toolTipDescription = this.props.toolTipDescription || "";

        const cardTitle = (
            <div className={"card__title"}>{title}{this.props.enableToolTip ? this.renderToolTip(title, toolTipDescription) : null}</div>
        );
        
        return (
            <div className={"cdb-table__header"}>
                <div className="card__header">
                    {this.props.hideHeaderTitle ? null : cardTitle}
                    {this.props.children}
                </div>
                <div className="cdb-table__row cdb-table__row--header">
                    {this.buildHeaderCells()}
                </div>
            </div>
        );
    }

    private buildLoading() {
        return (
            <div className="cdb-table__loading">
            <Spinner label="Loading..." labelPosition="right"/>
            </div>
        );
    }

    private buildEmpty() {
        return (
            <div className="cdb-table__empty">
                <span>{this.props.emptyMessage}</span>
            </div>
        )
    }

    private renderContent() {
        if (this.props.loading) {
            return this.buildLoading();
        }

        if (this.state.sortedItems.length === 0 && this.props.emptyMessage) {
            return this.buildEmpty();
        }

        return this.buildRowsOrVirtualizedList();
    }

    private renderToolTip(title: string, description: string) {

        const contentElement = (
            <span>
                <p className="text-smaller text-align-center"><b>{title}</b></p>
                <p className="text-tiny text-align-center">{description}</p>
            </span>
        );

        return (
            <TooltipHost
                content={contentElement}
                delay={TooltipDelay.zero}
                directionalHint={DirectionalHint.topCenter}
                styles={{ root: { display: 'inline-block' } }}
                calloutProps={{ gapSpace: 0 }}
                closeDelay={300}
            >
                <Icon iconName="Info" />
            </TooltipHost>
        );
    }



    private buildColumnsBanner(): JSX.Element | null {

        const showBanner = this.state.isSmallScreen && _.filter(this.props.columns, c => c.showSmallScreen === false).length > 0;
        if (!showBanner) {
            return null;
        }

        const bannerMessage = this.state.showLargeScreenColumnsEvenSmallScreen ? "Show Less Columns" : "Show All Columns";

        return (
            <div className={"cdb-table__banner"} onClick={() => { this.setState({ showLargeScreenColumnsEvenSmallScreen: !this.state.showLargeScreenColumnsEvenSmallScreen }) }}>
                <span>{bannerMessage}</span>
            </div>
        );
    }

    public render() {
        
        return (
            <div className={`cdb-table ${this.props.className ? this.props.className : ""}`}>

                {this.buildHeader()}

         
                {this.buildColumnsBanner()}

                {this.renderContent()}
            </div>
        );
    }
}